import React from 'react';

import styles from './stickyNoticeBar.module.css';

export const stickyNoticeTone = {
  info: 'info',
  warning: 'warning'
} as const;
export type StickyNoticeTone =
  (typeof stickyNoticeTone)[keyof typeof stickyNoticeTone];

export interface StickyNoticeAction {
  label: string;
  onClick: () => void;
}

export interface StickyNotice {
  /** Unique within the bar; also the row's test id suffix. */
  id: string;
  tone: StickyNoticeTone;
  message: React.ReactNode;
  action?: StickyNoticeAction;
}

export interface StickyNoticeBarProps {
  notices: StickyNotice[];
  /** Accessible name of the region. */
  label: string;
  className?: string;
}

/**
 * Bar that stays at the top of the viewport while the page scrolls, with one
 * row per notice in the order given. Renders nothing without notices.
 */
export const StickyNoticeBar = ({
  notices,
  label,
  className
}: StickyNoticeBarProps) => {
  if (notices.length === 0) {
    return null;
  }

  return (
    <section
      aria-label={label}
      className={className ? `${styles.bar} ${className}` : styles.bar}
      data-testid="sticky-notice-bar"
    >
      <ul className={styles.list}>
        {notices.map(({ id, tone, message, action }) => (
          <li
            key={id}
            className={`${styles.notice} ${styles[tone]}`}
            data-testid={`sticky-notice-${id}`}
          >
            <span className={styles.message}>{message}</span>
            {action && (
              <button
                type="button"
                className={styles.action}
                onClick={action.onClick}
              >
                {action.label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default StickyNoticeBar;
